/**
 * Single source of truth for the sidebar section names.
 *
 * Two surfaces render these labels and they must never diverge:
 *   1. The sidebar section headers (NavSidebar) — the collapsible groups
 *      "Manage" / "Analyze" / "Reports" / "Connect" / "Compliance", plus
 *      the pinned "Home" cluster.
 *   2. The page-header eyebrow (CgDataManagementPage `eyebrow`) on every routed page,
 *      which reads as the section the page's nav item lives under.
 *
 * Both surfaces upper-case the label in CSS, so a page's kicker always
 * renders identically to its sidebar section (e.g. `Manage` → `MANAGE`).
 * Rename a section here and the sidebar header and every page kicker move
 * together.
 *
 * ── i18n ──────────────────────────────────────────────────────────────────
 * SECTION_KICKERS still holds the English names, and ~20 pages still pass
 * `eyebrow={SECTION_KICKERS.ANALYZE}` (or the literal `eyebrow="Manage"`).
 * Rather than convert all of them, CgPageHeaderBlock resolves the eyebrow
 * through `sectionKickerKey()` below: a value that names a known section is
 * translated, anything else passes through untouched. That keeps the six
 * strings in one place — which was always the point of this file — and means
 * a page can still pass free text as an eyebrow if it ever needs to.
 */
export const SECTION_KICKERS = {
  HOME: 'Home',
  MANAGE: 'Manage',
  ANALYZE: 'Analyze',
  REPORTS: 'Reports',
  CONNECT: 'Connect',
  COMPLIANCE: 'Compliance',
};

// English defaults, enumerated once so the extractor sees all six literals in
// a single place instead of at every consumer.
export const SECTION_KICKER_DEFAULTS = {
  home: 'Home',
  manage: 'Manage',
  analyze: 'Analyze',
  reports: 'Reports',
  connect: 'Connect',
  compliance: 'Compliance',
};

// t('app:nav.section.home', 'Home')
// t('app:nav.section.manage', 'Manage')
// t('app:nav.section.analyze', 'Analyze')
// t('app:nav.section.reports', 'Reports')
// t('app:nav.section.connect', 'Connect')
// t('app:nav.section.compliance', 'Compliance')
//
// Map an eyebrow/section value onto its message key, or null when the value is
// not a section name (free-text eyebrows pass through untranslated).
export const sectionKickerKey = (value) => {
  if (typeof value !== 'string') return null;
  const slug = value.trim().toLowerCase();
  return slug in SECTION_KICKER_DEFAULTS ? `nav.section.${slug}` : null;
};

// Translate a section value. `t` must be bound to the `app` namespace.
// Returns the input unchanged when it is not a section name.
export const translateSectionKicker = (value, t) => {
  const key = sectionKickerKey(value);
  return key ? t(key, SECTION_KICKER_DEFAULTS[value.trim().toLowerCase()]) : value;
};

// The six section names in the active language, keyed as SECTION_KICKERS is.
export const getSectionKickers = (t) =>
  Object.fromEntries(
    Object.entries(SECTION_KICKERS).map(([name, english]) => [
      name,
      translateSectionKicker(english, t),
    ]),
  );

export default SECTION_KICKERS;
